/**
 * AdminGuard — wraps routes/pages that require a specific role.
 * Replaces inline `user?.role === 'super_admin'` checks across pages.
 *
 * Usage:
 *   <AdminGuard role="super_admin"><MyPage /></AdminGuard>
 *   <AdminGuard role={['super_admin', 'agency_owner']}>...</AdminGuard>
 */
import { useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";

type AllowedRole = "super_admin" | "agency_owner" | "manager" | "employee" | "client";

interface AdminGuardProps {
  role: AllowedRole | ReadonlyArray<AllowedRole>;
  children: React.ReactNode;
  redirectTo?: string;
}

export function AdminGuard({ role, children, redirectTo = "/dashboard" }: AdminGuardProps) {
  const { user, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  const allowed = Array.isArray(role) ? role : [role];
  const userRole = (user as { role?: string } | null | undefined)?.role;
  const hasAccess = userRole !== undefined && allowed.includes(userRole as AllowedRole);

  useEffect(() => {
    if (!isLoading && !hasAccess) {
      setLocation(redirectTo);
    }
  }, [isLoading, hasAccess, redirectTo, setLocation]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }

  if (!hasAccess) return null;
  return <>{children}</>;
}
